import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminMatchPlayerSummary } from '@shared/types';
import type { AdminMatchDetailSectionProps } from '@admin/games/types';
import Card from '@admin/components/Card';
import Pill from '@admin/components/Pill';
import { DataTable, type DataTableColumn } from '@admin/components/DataTable';
import { ApiError } from '@admin/services/api';
import { formatDateTime } from '@admin/utils/date';
import { getMatchRounds } from '../services/matchRounds';
import type {
  AdminMatchAnswer,
  AdminMatchRound,
  AdminMatchRoundsResponse,
} from '@games/word-clash/shared/types';

/**
 * Word Clash's rounds + answers view on the admin match detail page (#817 —
 * moved out of the framework's matches/Detail). Fetches
 * `GET /admin/matches/:id/rounds` (the server's `GameModule.adminMatchRounds`)
 * once the platform header has loaded, and renders every round's question,
 * per-player power-up spend and every answer submission.
 */
export default function MatchRoundsSection({ match }: AdminMatchDetailSectionProps) {
  const id = match.id;
  const [rounds, setRounds] = useState<
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; data: AdminMatchRoundsResponse }
    | { status: 'error'; message: string }
  >({ status: 'idle' });

  const fetchRounds = useCallback(async () => {
    setRounds({ status: 'loading' });
    try {
      const data = await getMatchRounds(id);
      setRounds({ status: 'ready', data });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load rounds';
      setRounds({ status: 'error', message });
    }
  }, [id]);

  useEffect(() => {
    void fetchRounds();
  }, [fetchRounds]);

  return (
    <section
      className="space-y-3"
      data-testid="match-detail-rounds-section"
    >
      <h2 className="text-sm font-semibold text-slate-900">Rounds</h2>
      {rounds.status === 'loading' && (
        <div
          data-testid="match-detail-rounds-loading"
          className="text-sm text-slate-500"
        >
          Loading rounds…
        </div>
      )}
      {rounds.status === 'error' && (
        <div
          role="alert"
          data-testid="match-detail-rounds-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {rounds.message}
        </div>
      )}
      {rounds.status === 'ready' && rounds.data.rounds.length === 0 && (
        <div
          data-testid="match-detail-rounds-empty"
          className="bg-white rounded border border-slate-200 p-4 text-sm text-slate-500"
        >
          No rounds yet.
        </div>
      )}
      {rounds.status === 'ready' &&
        rounds.data.rounds.map((r) => (
          <RoundCard
            key={r.id}
            round={r}
            player1={match.player1}
            player2={match.player2}
          />
        ))}
    </section>
  );
}

function RoundCard({
  round,
  player1,
  player2,
}: {
  round: AdminMatchRound;
  player1: AdminMatchPlayerSummary;
  player2: AdminMatchPlayerSummary;
}) {
  const winnerLabel =
    round.winnerId == null
      ? 'Draw'
      : round.winnerId === player1.id
        ? `P1 (${player1.displayName})`
        : round.winnerId === player2.id
          ? `P2 (${player2.displayName})`
          : round.winnerId;

  return (
    <Card
      padding="md"
      className="space-y-3"
      data-testid={`match-detail-round-${round.roundNumber}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900">
              Round {round.roundNumber}
            </h3>
            <Pill variant="neutral">{round.question.type}</Pill>
            <Pill variant="neutral">{round.question.difficulty}</Pill>
          </div>
          <div className="text-sm text-slate-700 mt-1">
            <span className="text-slate-500 text-xs uppercase tracking-wide mr-2">
              EN
            </span>
            {round.question.promptEn}
          </div>
          <div className="text-sm text-slate-700">
            <span className="text-slate-500 text-xs uppercase tracking-wide mr-2">
              TR
            </span>
            {round.question.promptTr}
          </div>
        </div>
        <div className="text-right text-sm">
          <div className="font-mono text-slate-900">
            {round.player1Points} – {round.player2Points}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Winner: {winnerLabel}
          </div>
        </div>
      </div>

      {/* Power-up spend counts per player --------------------------- */}
      <div className="grid grid-cols-2 gap-3">
        <PowerUpBlock
          slot="P1"
          name={player1.displayName}
          joker={round.player1JokerCount}
          timeBoost={round.player1TimeBoostCount}
          changeQuestion={round.player1ChangeQuestionCount}
        />
        <PowerUpBlock
          slot="P2"
          name={player2.displayName}
          joker={round.player2JokerCount}
          timeBoost={round.player2TimeBoostCount}
          changeQuestion={round.player2ChangeQuestionCount}
        />
      </div>

      <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
        <span>Started: {formatDateTime(round.startedAt)}</span>
        <span>
          Ended: {round.endedAt ? formatDateTime(round.endedAt) : '—'}
        </span>
      </div>

      <AnswersTable answers={round.answers} />
    </Card>
  );
}

function PowerUpBlock({
  slot,
  name,
  joker,
  timeBoost,
  changeQuestion,
}: {
  slot: 'P1' | 'P2';
  name: string;
  joker: number;
  timeBoost: number;
  changeQuestion: number;
}) {
  return (
    <Card tone="muted" padding="sm">
      <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
        {slot} · {name}
      </div>
      <div className="flex flex-wrap gap-2 text-xs font-mono text-slate-700">
        <span>Joker: {joker}</span>
        <span>Time: {timeBoost}</span>
        <span>Change: {changeQuestion}</span>
      </div>
    </Card>
  );
}

function AnswersTable({ answers }: { answers: readonly AdminMatchAnswer[] }) {
  const columns: DataTableColumn<AdminMatchAnswer>[] = useMemo(
    () => [
      {
        key: 'turnNumber',
        header: 'Turn',
        render: (a) => (
          <span className="font-mono text-xs text-slate-700">
            {a.turnNumber}
          </span>
        ),
      },
      {
        key: 'user',
        header: 'Player',
        render: (a) => (
          <Link
            to={`/users/${encodeURIComponent(a.userId)}`}
            className="text-sm text-slate-700 hover:text-slate-900 hover:underline"
            data-testid={`match-detail-answer-user-${a.id}`}
          >
            {a.userDisplayName}
          </Link>
        ),
      },
      {
        key: 'answerText',
        header: 'Answer',
        render: (a) => (
          <div className="flex flex-col">
            <span className="text-sm text-slate-900">{a.answerText || '—'}</span>
            {a.normalizedText &&
              a.normalizedText !== a.answerText.toLowerCase() && (
                <span className="text-xs text-slate-500 font-mono">
                  {a.normalizedText}
                </span>
              )}
          </div>
        ),
      },
      {
        key: 'isValid',
        header: 'Result',
        render: (a) =>
          a.isValid ? (
            <Pill variant="success" testId="answer-valid">
              +{a.pointsAwarded}
            </Pill>
          ) : (
            <Pill variant="danger">{a.rejectionReason ?? 'invalid'}</Pill>
          ),
      },
      {
        key: 'validationMethod',
        header: 'Match',
        render: (a) => (
          <span className="font-mono text-xs text-slate-500">
            {a.validationMethod ?? '—'}
          </span>
        ),
      },
      {
        key: 'submittedAt',
        header: 'Submitted',
        render: (a) => (
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {formatDateTime(a.submittedAt)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <DataTable<AdminMatchAnswer>
      columns={columns}
      rows={answers}
      rowKey={(a) => a.id}
      empty="No answers submitted."
      testId="match-detail-answers-table"
    />
  );
}
